import { useEffect, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ProtectPainter, protectUniforms } from '../utils/protectPainter';
import { getViewportBridge } from '../utils/viewportBridge';
import '../utils/supportGenerator';
import type { ProtectSettings } from '../types/protect';

/**
 * A small, self-contained paintable viewport for the "What changed?" overlay. It clones the
 * project's model (own geometry, shared materials) so painting here never touches the real build.
 */
export function useProtectDemo(containerRef: RefObject<HTMLDivElement>, settings: ProtectSettings, modelReady: boolean) {
  const painterRef = useRef<ProtectPainter | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    painterRef.current?.configure(settings);
  }, [settings]);

  useEffect(() => {
    const container = containerRef.current;
    const source = getViewportBridge()?.getModel();
    if (!container || !modelReady || !source) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#e5e8eb');
    scene.add(new THREE.HemisphereLight('#ffffff', '#8d939a', 1.6));
    const key = new THREE.DirectionalLight('#ffffff', 1.8);
    key.position.set(60, 120, 80);
    scene.add(key);
    const fill = new THREE.DirectionalLight('#ffffff', 0.6);
    fill.position.set(-80, 40, -60);
    scene.add(fill);

    // Clone with independent geometry so the protect attribute is ours alone.
    const sourceMeshes: THREE.Mesh[] = [];
    source.traverse((o) => (o as THREE.Mesh).isMesh && sourceMeshes.push(o as THREE.Mesh));
    const model = source.clone(true);
    let mi = 0;
    model.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const src = sourceMeshes[mi++];
      mesh.geometry = mesh.geometry.clone();
      mesh.geometry.computeBoundsTree();
      mesh.material = src.material;
      if (src.userData.original) mesh.userData.original = src.userData.original;
    });
    const holder = new THREE.Group();
    holder.add(model);
    scene.add(holder);
    holder.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(holder);
    const center = box.getCenter(new THREE.Vector3());
    holder.position.sub(center);
    const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 40;

    const camera = new THREE.PerspectiveCamera(35, 1, 0.5, 4000);
    camera.position.set(radius * 1.7, radius * 1.3, radius * 2.2);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.minDistance = radius * 1.4;
    controls.maxDistance = radius * 6;

    const painter = new ProtectPainter(model, scene);
    painter.clearAll();
    painter.save();
    painter.configure(settingsRef.current);
    const prevShow = protectUniforms.uProtectShow.value;
    painter.setVisible(true);
    painterRef.current = painter;
    setLoaded(true);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = container;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);

    const raycaster = new THREE.Raycaster();
    raycaster.firstHitOnly = true;
    const pointer = new THREE.Vector2();
    const hitAt = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set((e.clientX - rect.left) / rect.width * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      return raycaster.intersectObject(model, true)[0] ?? null;
    };

    let painting = false;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const h = hitAt(e);
      if (!h) return;
      painting = true;
      controls.enabled = false;
      painter.strokeStart(h);
    };
    const onMove = (e: PointerEvent) => {
      if (e.target !== renderer.domElement && !painting) {
        painter.hover(null);
        return;
      }
      const h = hitAt(e);
      painter.hover(h);
      renderer.domElement.style.cursor = h ? 'crosshair' : 'grab';
      if (painting && h) painter.strokeTo(h);
    };
    const onUp = () => {
      if (!painting) return;
      painting = false;
      controls.enabled = true;
      painter.strokeEnd();
    };
    const onLeave = () => !painting && painter.hover(null);
    container.addEventListener('pointerdown', onDown, { capture: true });
    container.addEventListener('pointerleave', onLeave);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);

    const clock = new THREE.Clock();
    let frame = 0;
    const loop = () => {
      frame = requestAnimationFrame(loop);
      painter.update(clock.getDelta());
      controls.update();
      renderer.render(scene, camera);
    };
    loop();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      container.removeEventListener('pointerdown', onDown, { capture: true });
      container.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      painter.dispose();
      painterRef.current = null;
      protectUniforms.uProtectShow.value = prevShow;
      model.traverse((o) => (o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry.dispose());
      controls.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
      setLoaded(false);
    };
  }, [containerRef, modelReady]);

  return { painterRef, loaded };
}
